import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Card, PageHeader, Pill, EmptyState } from "@/components/common";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { CheckSquare, Check, X, Pencil, Package, Megaphone, Sparkles, ShieldAlert } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TYPE_META = {
  product: { icon: Package, label: "Publish Product", tone: "emerald" },
  ad: { icon: Megaphone, label: "Ad Spend", tone: "amber" },
  pricing: { icon: Sparkles, label: "Pricing", tone: "blue" },
};

export default function ReviewQueue() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [feedbackMode, setFeedbackMode] = useState("reject");
  const [feedback, setFeedback] = useState("");
  const { settings } = useSettings();
  const paused = settings?.kill_switch;

  const load = useCallback(() => {
    api.get("/queue", { params: { status } }).then(({ data }) => setItems(data)).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const approve = async (item) => {
    try {
      await api.post(`/queue/${item.id}/approve`);
      toast.success(item.type === "ad" ? "Ad campaign approved & launched." : "Approved & published live.");
      window.dispatchEvent(new Event("queue-changed"));
      load();
    } catch (e) {
      toast.error(apiError(e, "Approve failed"));
    }
  };

  const openFeedback = (item, mode) => { setFeedbackFor(item); setFeedbackMode(mode); setFeedback(""); };

  const submitFeedback = async () => {
    try {
      await api.post(`/queue/${feedbackFor.id}/reject`, { feedback: feedback || (feedbackMode === "edit" ? "Requested changes" : "Rejected") });
      toast[feedbackMode === "edit" ? "message" : "error"](feedbackMode === "edit" ? "Sent back with change requests." : "Rejected.");
      window.dispatchEvent(new Event("queue-changed"));
      setFeedbackFor(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    }
  };

  return (
    <>
      <PageHeader title="Review Queue" subtitle="Approve, edit or reject every money-out or publish action. Nothing goes live without you." testid="queue-header">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList className="bg-[#12161F] border border-slate-800">
            <TabsTrigger value="pending" data-testid="queue-tab-pending" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">Pending</TabsTrigger>
            <TabsTrigger value="approved" data-testid="queue-tab-approved" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="queue-tab-rejected" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>
      </PageHeader>

      {paused && (
        <div className="flex items-center gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2.5" data-testid="queue-paused-warning">
          <ShieldAlert className="w-4 h-4" /> Kill switch active — approvals are blocked until you resume agents.
        </div>
      )}

      {items.length === 0 ? (
        <Card className="p-6"><EmptyState icon={CheckSquare} title={`No ${status} items`} hint="Assemble products in the Design Studio or propose ad campaigns to populate the queue." /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((item) => {
            const meta = TYPE_META[item.type] || TYPE_META.product;
            const Icon = meta.icon;
            return (
              <Card key={item.id} className="p-5" data-testid={`queue-item-${item.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-slate-800/70 border border-slate-700/60`}><Icon className="w-4.5 h-4.5 text-slate-300" /></div>
                    <div>
                      <Pill tone={meta.tone}>{meta.label}</Pill>
                      <h3 className="font-head text-sm font-semibold text-slate-100 mt-1 max-w-xs truncate">{item.title}</h3>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-head font-bold text-emerald-400">{Math.round((item.confidence || 0) * 100)}%</div>
                    <div className="text-[10px] text-slate-500 font-mono uppercase">AI Confidence</div>
                  </div>
                </div>

                {item.payload?.image && (
                  <div className="mt-3 flex gap-3">
                    <img src={item.payload.image} alt="" className="w-20 h-20 rounded-lg object-cover border border-slate-800" />
                    <div className="text-sm text-slate-400 space-y-1">
                      {item.payload.price != null && <div>Price <span className="text-slate-200 font-medium">${item.payload.price}</span> · Cost ${item.payload.cost}</div>}
                      {item.payload.margin != null && <div>Margin <span className="text-emerald-300">${item.payload.margin}</span></div>}
                      {item.payload.product_type && <div className="capitalize">{item.payload.product_type}</div>}
                    </div>
                  </div>
                )}
                {item.payload?.budget != null && (
                  <div className="mt-3 text-sm text-slate-400">Budget <span className="text-slate-200 font-medium">${item.payload.budget}/day</span> on {item.payload.channel} · Est. ROAS <span className="text-emerald-300">{item.payload.est_roas}x</span></div>
                )}

                <div className="mt-3 rounded-lg bg-[#0C0F14] border border-slate-800/70 p-3">
                  <div className="text-[10px] text-slate-500 font-mono uppercase tracking-wider mb-1">Why the agent recommends this</div>
                  <p className="text-sm text-slate-300">{item.summary}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Pill tone={item.risk === "high" ? "red" : item.risk === "medium" ? "amber" : "emerald"}>{item.risk} risk</Pill>
                    <Pill tone={item.recommend ? "emerald" : "amber"}>{item.recommend ? "AI: approve" : "AI: review"}</Pill>
                  </div>
                </div>

                {item.status === "pending" ? (
                  <div className="flex gap-2 mt-4">
                    <button data-testid={`approve-card-button-${item.id}`} onClick={() => approve(item)} disabled={paused} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 disabled:opacity-40 transition-colors">
                      <Check className="w-4 h-4" /> Approve
                    </button>
                    <button data-testid={`edit-card-button-${item.id}`} onClick={() => openFeedback(item, "edit")} className="px-3 py-2 rounded-lg bg-slate-800/70 text-slate-200 hover:bg-slate-700 border border-slate-700/60 transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`reject-card-button-${item.id}`} onClick={() => openFeedback(item, "reject")} className="px-3 py-2 rounded-lg bg-red-950/50 text-red-300 hover:bg-red-900/60 border border-red-800/60 transition-colors"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="mt-4"><Pill tone={item.status === "approved" ? "emerald" : "red"}>{item.status}</Pill>
                    {item.feedback && <span className="text-xs text-slate-500 ml-2">“{item.feedback}”</span>}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!feedbackFor} onOpenChange={(o) => !o && setFeedbackFor(null)}>
        <DialogContent className="bg-[#12161F] border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle className="font-head">{feedbackMode === "edit" ? "Request changes" : "Reject action"}</DialogTitle>
            <DialogDescription className="text-slate-400">{feedbackMode === "edit" ? "Tell the agent what to change; it goes back for revision." : "Optionally add a reason. This action will not go live."}</DialogDescription>
          </DialogHeader>
          <textarea data-testid="feedback-input" value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={4} placeholder={feedbackMode === "edit" ? "What should the agent change? (tone, price, design…)" : "Reason for rejection (optional)"} className="w-full px-3 py-2.5 rounded-lg bg-[#0C0F14] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm resize-none" />
          <DialogFooter>
            <button data-testid="submit-feedback-button" onClick={submitFeedback} className={`px-4 py-2 rounded-lg font-semibold ${feedbackMode === "edit" ? "bg-amber-500 text-slate-950 hover:bg-amber-400" : "bg-red-600 text-white hover:bg-red-700"}`}>
              {feedbackMode === "edit" ? "Send back to agent" : "Reject"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
